import type { DatingInput } from "./pregnancy";

/**
 * The due-date calculator's pages: one per dating method, each worded as people search it.
 * The copy is what makes them worth indexing: a bare form is a thin page.
 */

export type DueDateMethod = DatingInput["method"];

export type DueDatePage = {
  method: DueDateMethod;
  path: string;
  /** The tab between the calculators. */
  tab: string;
  title: string;
  description: string;
  h1: string;
  intro: string;
  sections: { heading: string; paragraphs: string[] }[];
  faq: { question: string; answer: string }[];
};

const TERM_41 = {
  heading: "Pourquoi 41 SA en France et 40 SA ailleurs ?",
  paragraphs: [
    "En France, le terme se calcule à partir du début de grossesse, auquel on ajoute 9 mois : cela tombe à 41 semaines d'aménorrhée (SA). La plupart des autres pays, et beaucoup d'applications, retiennent 40 SA, soit 280 jours après les dernières règles.",
    "Les deux dates sont affichées ci-dessus : une semaine les sépare. Un accouchement est dit « à terme » dès 37 SA, et la grande majorité des bébés naissent dans les deux semaines qui entourent le terme.",
  ],
};

const SA_VS_SG = {
  heading: "Semaines d'aménorrhée ou semaines de grossesse ?",
  paragraphs: [
    "Les médecins et sages-femmes comptent en semaines d'aménorrhée (SA), depuis le premier jour des dernières règles. Les semaines de grossesse (SG) partent de la fécondation, environ deux semaines plus tard : 12 SA correspondent donc à 10 SG.",
  ],
};

export const DUE_DATE_PAGES: Record<DueDateMethod, DueDatePage> = {
  lmp: {
    method: "lmp",
    path: "/calcul-date-accouchement",
    tab: "Dernières règles",
    title: "Calcul de la date d'accouchement (terme) · Listou",
    description:
      "Calculez votre date prévue d'accouchement à partir de vos dernières règles : terme à 41 SA, semaines d'aménorrhée et dates des échographies.",
    h1: "Calculer sa date d'accouchement",
    intro:
      "Indiquez le premier jour de vos dernières règles et la durée habituelle de votre cycle : vous obtenez la date prévue d'accouchement, votre avancée en semaines d'aménorrhée et les dates de vos trois échographies.",
    sections: [
      {
        heading: "Comment se calcule le terme ?",
        paragraphs: [
          "L'ovulation a lieu environ 14 jours avant les règles suivantes. Avec un cycle de 28 jours, la fécondation se situe donc 14 jours après le début des dernières règles ; avec un cycle de 32 jours, 18 jours après. Le terme correspond à ce début de grossesse plus 9 mois.",
          "Si votre cycle est irrégulier ou si vous ne connaissez pas la date de vos dernières règles, c'est l'échographie du premier trimestre qui fixera la date officielle.",
        ],
      },
      SA_VS_SG,
      TERM_41,
    ],
    faq: [
      {
        question: "Mon terme peut-il changer ?",
        answer:
          "Oui. L'échographie du premier trimestre, entre 11 et 14 SA, mesure le bébé et fixe la date de début de grossesse qui fait foi. Elle peut décaler le terme de quelques jours par rapport au calcul fait sur les règles.",
      },
      {
        question: "Quelle est la durée d'une grossesse ?",
        answer:
          "Environ 9 mois après la fécondation, soit 41 SA selon le calcul français, ou 39 semaines de grossesse.",
      },
      {
        question: "Ce calcul remplace-t-il l'avis d'un professionnel ?",
        answer:
          "Non. Il donne une date indicative ; seuls votre sage-femme ou votre médecin fixent le terme, en s'appuyant sur l'échographie.",
      },
    ],
  },
  conception: {
    method: "conception",
    path: "/calcul-date-accouchement/date-de-conception",
    tab: "Date de conception",
    title: "Calcul du terme à partir de la date de conception · Listou",
    description:
      "Vous connaissez la date de conception ? Calculez la date prévue d'accouchement, vos semaines d'aménorrhée et les dates des échographies.",
    h1: "Calculer le terme à partir de la date de conception",
    intro:
      "Si vous connaissez le jour de la conception (suivi d'ovulation, rapport unique), c'est la méthode la plus directe : le terme tombe 9 mois plus tard.",
    sections: [
      {
        heading: "Conception, fécondation : quelle date indiquer ?",
        paragraphs: [
          "Les spermatozoïdes survivent jusqu'à cinq jours : la fécondation peut suivre le rapport de quelques jours. Si vous hésitez, indiquez le jour de l'ovulation, repéré par un test ou une courbe de température.",
          "Faute de date sûre, le calcul par les dernières règles reste la référence avant l'échographie du premier trimestre.",
        ],
      },
      SA_VS_SG,
      TERM_41,
    ],
    faq: [
      {
        question: "Combien de temps après la conception a lieu l'accouchement ?",
        answer:
          "Environ 9 mois, soit 39 semaines de grossesse. En semaines d'aménorrhée, on ajoute les deux semaines qui précèdent la fécondation : 41 SA.",
      },
      {
        question: "Peut-on connaître la date de conception à partir du terme ?",
        answer:
          "Oui, en retirant 9 mois au terme calculé en France. Le résultat reste approximatif : il dépend de la précision de la datation.",
      },
    ],
  },
  ultrasound: {
    method: "ultrasound",
    path: "/calcul-date-accouchement/echographie",
    tab: "Échographie",
    title: "Calcul du terme d'après l'échographie de datation · Listou",
    description:
      "Reportez l'âge indiqué par votre échographie (en SA) pour obtenir la date prévue d'accouchement et les dates des échographies suivantes.",
    h1: "Calculer le terme d'après l'échographie",
    intro:
      "Le compte rendu de l'échographie indique l'âge de la grossesse le jour de l'examen, en semaines et jours d'aménorrhée (par exemple 12 SA + 3 j). Reportez-le ici avec la date de l'examen.",
    sections: [
      {
        heading: "Pourquoi l'échographie du premier trimestre fait foi",
        paragraphs: [
          "Entre 11 et 14 SA, tous les embryons grandissent à la même vitesse : la mesure de la longueur cranio-caudale (du sommet du crâne aux fesses) donne l'âge de la grossesse à 3 à 5 jours près. C'est cette date qui est retenue pour le suivi, même si elle diffère du calcul par les règles.",
          "Les échographies suivantes ne corrigent plus le terme : les bébés grandissent ensuite à des rythmes différents.",
        ],
      },
      SA_VS_SG,
      TERM_41,
    ],
    faq: [
      {
        question: "L'échographie a donné une date différente de mon calcul, laquelle croire ?",
        answer:
          "Celle de l'échographie du premier trimestre. Un cycle plus long ou une ovulation tardive suffisent à décaler le calcul par les règles.",
      },
      {
        question: "Et si ma première échographie a eu lieu après 14 SA ?",
        answer:
          "La datation est alors moins précise. Votre sage-femme ou votre médecin s'appuiera sur plusieurs mesures pour estimer le terme.",
      },
    ],
  },
  ivf: {
    method: "ivf",
    path: "/calcul-date-accouchement/fiv",
    tab: "FIV",
    title: "Calcul du terme après une FIV (transfert J3 ou J5) · Listou",
    description:
      "Grossesse par FIV ou transfert d'embryon congelé : calculez le terme à partir de la date du transfert et de l'âge de l'embryon (J3 ou J5).",
    h1: "Calculer le terme après une FIV",
    intro:
      "Après une fécondation in vitro, la date de fécondation est connue avec précision. Indiquez la date du transfert et l'âge de l'embryon transféré.",
    sections: [
      {
        heading: "Transfert à J3 ou à J5",
        paragraphs: [
          "Un embryon transféré à J3 a été fécondé trois jours avant le transfert ; un blastocyste transféré à J5, cinq jours avant. Le début de grossesse est donc la date du transfert moins trois ou cinq jours.",
          "Le calcul est le même pour un transfert d'embryon congelé (TEC) : seul compte l'âge de l'embryon au moment du transfert.",
        ],
      },
      SA_VS_SG,
      TERM_41,
    ],
    faq: [
      {
        question: "Le terme d'une grossesse par FIV est-il différent ?",
        answer:
          "Non : il se calcule de la même façon, 9 mois après la fécondation. Il est simplement plus précis, puisque la date de fécondation est connue.",
      },
      {
        question: "L'échographie du premier trimestre peut-elle modifier ce terme ?",
        answer:
          "Rarement : après une FIV, la datation par le transfert est déjà très fiable, et l'équipe médicale la conserve en général.",
      },
    ],
  },
};

export const DUE_DATE_ORDER: DueDateMethod[] = ["lmp", "conception", "ultrasound", "ivf"];

/** The URL segment under /calcul-date-accouchement for each method but the hub's. */
export const DUE_DATE_SLUGS: Record<Exclude<DueDateMethod, "lmp">, string> = {
  conception: "date-de-conception",
  ultrasound: "echographie",
  ivf: "fiv",
};
